import dayjs from 'dayjs';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { CareMapState } from '../../caremap/CareMapState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { isPlatform } from '../../utils/isPlatform';
import { blobToBase64 } from '../../utils/blobToBase64';
import { ITrackingFrom } from '../ITrackingFrom';
import { CARE_CODE_SOURCE, ViewerUrlProvider } from '../ViewerUrlProvider';
import { CareCodeDocumentBuilder } from './CareCodeDocumentBuilder';

/**
 * A Care Code is printed and left in someone's home, so it is deliberately long lived. The
 * viewer token it carries is a bearer credential for that whole window and the API has no
 * way to revoke one early, so this is the only control over how long a sheet keeps working.
 * The button copy and the printed expiry line both describe this window in words, so they
 * have to move with it.
 */
export const CARE_CODE_DURATION_DAYS = 365;

// FileOpener's FILE_NOT_SUPPORTED, raised on Android when nothing is installed to view a PDF.
const FILE_NOT_SUPPORTED = '8';

export class CareCodeService {
    static inject = () => [CareMapState, ViewerUrlProvider, CareCodeDocumentBuilder, MixPanelEventEmitter];
    constructor(
        private careMapState: CareMapState,
        private viewerUrlProvider: ViewerUrlProvider,
        private documentBuilder: CareCodeDocumentBuilder,
        private mixpanel: MixPanelEventEmitter
    ) {}

    private isCreating = false;

    /**
     * Mints a viewer token, renders the printable sheet around it, and hands the file to the
     * platform viewer. The viewer is what gives the user share and print, so there is no
     * in-app share button to keep in step with it.
     */
    createAndOpen = async ({ from }: { from: ITrackingFrom; }): Promise<void> => {
        // The guard lives here rather than in the button because dismissing the Sharing sheet
        // unmounts the button mid-generation; reopening it would otherwise present an enabled
        // button and a second press would mint a second token that nobody can revoke.
        if (this.isCreating) {
            return;
        }
        this.isCreating = true;

        try {
            await this.create({ from });
        } finally {
            this.isCreating = false;
        }
    };

    private create = async ({ from }: { from: ITrackingFrom; }) => {
        // Read the patient off the active caremap before any await, so the sheet cannot end up
        // describing a different care recipient than the one the token was minted for. The
        // Sharing sheet is mounted globally and survives navigation, so the active caremap can
        // change underneath an in-flight generation.
        const caremap = this.careMapState.getActiveCaremap();
        const patient = caremap?.patient || {};
        const filename = this.buildFilename();

        // Rasterizing the logo and the care recipient's photo can fail, and a minted token cannot
        // be revoked, so get the fallible asset work out of the way while there is nothing to leak.
        await this.documentBuilder.prepareAssets({ patient });

        const expiresAt = dayjs().add(CARE_CODE_DURATION_DAYS, 'days').toISOString();

        // buildViewerUrl's access_token param carries the session token, matching how the
        // existing view-only share link is built.
        const { session_token } = await this.careMapState.createViewerAccessToken(CARE_CODE_DURATION_DAYS);
        const viewerUrl = this.viewerUrlProvider.buildViewerUrl({ access_token: session_token, careMapId: caremap?.careMapId, source: CARE_CODE_SOURCE });

        const blob = await this.documentBuilder.build({ viewerUrl, patient, expiresAt });

        this.mixpanel.track('Care Code Created', () => ({
            duration: CARE_CODE_DURATION_DAYS,
            from
        }));

        await this.open({ blob, filename });
    };

    private buildFilename = () => {
        const fullName = this.careMapState.getPatientFullName();
        const prefix = fullName ? `${fullName} ` : '';

        // Slashes and colons break file paths on at least one of the platforms we write to.
        return `${prefix}Care Code ${dayjs().format('YYYY-MM-DD')}.pdf`.replace(/[/\\:]/g, '-');
    };

    private open = async ({ blob, filename }: { blob: Blob; filename: string; }) => {
        if (isPlatform('capacitor')) {
            await this.openNatively({ blob, filename });
        } else {
            this.downloadInBrowser({ blob, filename });
        }
    };

    private openNatively = async ({ blob, filename }: { blob: Blob; filename: string; }) => {
        // Cache rather than Data: the sheet can always be regenerated, so it shouldn't take up
        // permanent space on the device.
        const { uri } = await Filesystem.writeFile({
            path: filename,
            data: await blobToBase64(blob),
            directory: Directory.Cache
        });

        try {
            await FileOpener.open({ filePath: uri, contentType: 'application/pdf', openWithDefault: true });
        } catch (err: any) {
            if (err?.code === FILE_NOT_SUPPORTED) {
                // The file is in the app's private cache, so with no PDF viewer installed the user
                // has no way to reach it. Say so rather than surfacing a bare plugin error.
                throw new Error('This device has no app installed that can open a PDF. Install a PDF viewer and try again.');
            }

            throw err;
        }
    };

    /**
     * Always downloads rather than opening a tab. window.open only works inside the user gesture
     * that triggered it, and by this point the token round trip and the render have both been
     * awaited, so Safari blocks it outright and Chrome blocks it on a slow connection. Blockers
     * do not reliably return null either, so a "did the tab open?" fallback cannot be trusted.
     * This matches how the care map PNG export reaches the user.
     */
    private downloadInBrowser = ({ blob, filename }: { blob: Blob; filename: string; }) => {
        const objectUrl = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.setAttribute('download', filename);
        anchor.setAttribute('href', objectUrl);

        // Firefox only honours a download anchor that is in the document.
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        URL.revokeObjectURL(objectUrl);
    };

}
